import type { ReactNode } from 'react';
import { KText } from '@/ui-library/components';
import moment from 'moment-timezone';
import { BusyEventContent } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewCalendar/Components/BusyEventContent';
import { ManualAvailableContent } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewCalendar/Components/ManualAvailableContent';
import { ManualBookedContent } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewCalendar/Components/ManualBookedContent';
import { MeetingRoomEventContent } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewCalendar/Components/MeetingRoomEventContent';
import { SelfFreeSlot } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewCalendar/Components/SelfFreeSlot';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';

const formatTimeRangeForDay = (
  event,
  eventInfo,
  is12HourFormat = true,
  timezone = ''
) => {
  // Parse event times in the calendar's timezone so display stays aligned with the grid
  const tz = timezone || moment.tz.guess();
  const originalStart = moment.tz(event.start, tz);
  const originalEnd = moment.tz(event.end, tz);
  const originalStartDay = originalStart.format('YYYY-MM-DD');
  const originalEndDay = originalEnd.format('YYYY-MM-DD');

  const timeFormat = is12HourFormat ? 'h:mm A' : 'HH:mm';
  const formatTime = (m) => m.format(timeFormat);

  const midnightStart = is12HourFormat ? '12:00 AM' : '00:00';
  const midnightEnd = is12HourFormat ? '11:59 PM' : '23:59';

  // Check if original event spans multiple days (in calendar timezone)
  if (originalStartDay !== originalEndDay) {
    // Use FullCalendar's segment flags to determine which day segment we're rendering
    // isStart=true means this segment contains the original event's start
    // isEnd=true means this segment contains the original event's end
    const isStart = eventInfo.isStart !== false;
    const isEnd = eventInfo.isEnd !== false;

    if (isStart && !isEnd) {
      // First day segment: show original start time to 11:59 PM
      return `${formatTime(originalStart)} - ${midnightEnd}`;
    } else if (!isStart && isEnd) {
      // Last day segment: show 12:00 AM to original end time
      return `${midnightStart} - ${formatTime(originalEnd)}`;
    } else if (!isStart && !isEnd) {
      // Middle day segment: full day
      return `${midnightStart} - ${midnightEnd}`;
    }
    // Fallback: event fits entirely in one segment (isStart && isEnd)
    return `${formatTime(originalStart)} - ${formatTime(originalEnd)}`;
  }

  // Single day: return formatted time range
  return `${formatTime(originalStart)} - ${formatTime(originalEnd)}`;
};

export const SchEventContentRenderer = ({
  eventInfo,
  registerForUpdates = undefined,
  interviewDataRef,
  updateSharedData,
  removeSlot = undefined,
  onSelect = undefined,
  timezone,
}) => {
  const { event } = eventInfo;
  const currUser = useSelector(selectCurrUser as any);
  /** @ts-expect-error - hour format */
  const is12HourFormat = currUser?.time_fmt === 'hour_12';
  const type = event.extendedProps.type;

  const { bookedSlot } = interviewDataRef.current;

  const commonProps = {
    eventInfo,
    registerForUpdates,
    interviewDataRef,
    updateSharedData,
    timezone,
  };

  switch (type) {
    case 'manual-available':
      return (
        <ManualAvailableContent
          {...commonProps}
          createNewEvent={(obj) => {
            updateSharedData('bookedSlot', { ...bookedSlot, ...obj });
          }}
        />
      );
    case 'free-slot':
      return <SelfFreeSlot removeSlot={removeSlot} {...commonProps} />;
    case 'busy':
      return (
        <BusyEventContent
          onSelect={onSelect}
          interviewDataRef={interviewDataRef}
          {...commonProps}
        />
      );
    case 'booked':
      return <ManualBookedContent {...commonProps} />;
    case 'meeting_room':
      return <MeetingRoomEventContent {...commonProps} />;
    case 'candidate-availability': {
      const { candidateName } = event.extendedProps || {};
      const formattedTimeRange = formatTimeRangeForDay(
        event,
        eventInfo,
        is12HourFormat,
        timezone
      );

      const tooltipLabel: ReactNode = (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <p style={{ fontWeight: 600 }}>Available</p>
          <p>{formattedTimeRange}</p>
          {candidateName && <p>{candidateName}</p>}
        </div>
      );

      return (
        <ManualAvailableContent
          {...commonProps}
          createNewEvent={(obj) => {
            updateSharedData('bookedSlot', { ...bookedSlot, ...obj });
          }}
          slotContent={({ _noOfLines: noOfLines, _isNarrow: isNarrow }) =>
            isNarrow ? null : (
              <>
                <KText size="xs-semi-bold" color="pastel-2-dark" noOfLines={1}>
                  Available
                </KText>
                <KText
                  size="xs-semi-bold"
                  color="pastel-2-dark"
                  noOfLines={Math.max(1, noOfLines - 2)}
                >
                  {formattedTimeRange}
                </KText>
                {candidateName && (
                  <KText
                    size="xs-semi-bold"
                    color="pastel-2-dark"
                    lineHeight="1.3"
                    noOfLines={1}
                  >
                    {candidateName}
                  </KText>
                )}
              </>
            )
          }
          tooltipLabel={tooltipLabel}
          contentBoxProps={{ p: '2px 4px' }}
        />
      );
    }
    default:
      return null;
  }
};
